import { ActivityIndicator, Pressable, View } from "@-/com.npmjs.react-native";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { IconButtonHeightContext } from "@-/dev.improve.haniverse.components.ui.button.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import type { ReactNode } from "react";
import { useToolbarMenuBandHeight } from "#use-toolbar-menu-band-height";

/**
 * How far the page title's capitals sit below its line box's center, which is where the close
 * button's ✕ is centered. The line box centers Han Composite's ascent/descent (1160/288 of 1000
 * units per em), so its midpoint stands 0.436em above the baseline while the capitals (OS/2
 * sCapHeight 714) center 0.357em above it. At the title's `text-lg` (18px) that is 1.42px, which
 * matches the iOS `onTextLayout` metrics (ascender 21.848, capHeight 12.852, line height 28).
 */
const TITLE_CAP_CENTER_DROP = ((1160 - 288) / 2 / 1000 - 714 / 2 / 1000) * 18;

/** The menu band's leading close control, spinning in place while the menu's view recomputes. */
export function ToolbarMenuCloseButton({
  close,
  closeLabel,
  isLoading,
}: {
  close: () => void;
  closeLabel: string;
  isLoading: boolean;
}) {
  const { expandedControlDiameter } = useToolbarGridMetrics();
  return (
    <Pressable
      accessibilityLabel={closeLabel}
      accessibilityRole="button"
      accessibilityState={{ busy: isLoading, expanded: true }}
      className="shrink-0 items-center justify-center active:opacity-60"
      hitSlop={8}
      onPress={() => close()}
      style={{ height: expandedControlDiameter, width: expandedControlDiameter }}
    >
      {isLoading ? (
        <ActivityIndicator size="small" />
      ) : (
        <Icon className="text-foreground" name="LuX" size={24} />
      )}
    </Pressable>
  );
}

/**
 * A titled menu page's header band: the close button, then `title`, then `children` as trailing
 * controls. The More menu's page layout and the swipe-down Debug panel share it, so every surface
 * opened from a toolbar button opens onto the same header.
 */
export function ToolbarMenuPageHeader({
  children,
  close,
  closeLabel,
  isLoading = false,
  onLayout,
  title,
  titleTestID,
}: {
  children?: ReactNode;
  close: () => void;
  closeLabel: string;
  isLoading?: boolean;
  onLayout?: () => void;
  title: string;
  titleTestID?: string;
}) {
  const { expandedControlDiameter } = useToolbarGridMetrics();
  const bandHeight = useToolbarMenuBandHeight();
  return (
    <View
      className="shrink-0 flex-row items-center gap-1 px-1.75 pt-1.75 pb-0.75"
      onLayout={onLayout}
      style={{ height: bandHeight }}
    >
      <ToolbarMenuCloseButton close={close} closeLabel={closeLabel} isLoading={isLoading} />
      <Text
        accessibilityRole="header"
        className="min-w-0 flex-1 text-lg font-semibold text-foreground"
        numberOfLines={1}
        style={{ transform: [{ translateY: -TITLE_CAP_CENTER_DROP }] }}
        testID={titleTestID}
      >
        {title}
      </Text>
      <View className="shrink-0 flex-row items-center gap-1">
        <IconButtonHeightContext value={expandedControlDiameter}>
          {children}
        </IconButtonHeightContext>
      </View>
    </View>
  );
}
